import type { Cash, MC, MQ } from './money';

export const ACTIVE_STATUSES = ['resting', 'open', 'partially_filled'] as const;
export const WS_ORDER_ACTIVE = ['resting', 'open', 'partially_filled', 'partial_fill'] as const;

export type OrderSource = 'manual' | 'reconciled' | 'bracket' | 'bracket-hedge' | 'external';

export interface Order {
  order_id: string;
  client_order_id: string;
  ticker: string;
  status: string;
  side: 'yes' | 'no' | '';
  yes_price_mc: MC | null;
  no_price_mc: MC | null;
  remaining_mq: MQ;
  created_time?: string;
  queue_position: number | null;
  source: OrderSource;
  account_id: string | null;
  lifecycle_state: 'open' | 'partially_filled' | 'filled' | 'canceled';
  /** performance.now() at creation — drives the reconcile grace window. */
  local_ts: number;
  /** Original placed quantity, for bracket fill math. */
  orig_mq: MQ | null;
}

export interface MarketState {
  yesBids: Map<MC, MQ>;
  noBids: Map<MC, MQ>;
  myOrders: Map<string, Order>;
  /** Estimated contracts (MQ) ahead of each order in the queue, by order_id. */
  estAhead: Map<string, MQ>;
  bestYesBid: MC | null;
  bestNoBid: MC | null;
  bestYesAsk: MC | null;
  bestNoAsk: MC | null;
  /** Mid in tenths of MC, so the integer average of two MC prices stays exact. */
  midYesDeci: number | null;
  openOrders: number;
  bidOrders: number;
  askOrders: number;
  positionMq: MQ;
  avgCostMc: number;
  realizedCash: Cash;
  feesCash: Cash;
  priceLevelStructure: string;
  priceRanges: { start: string; end: string; step: string }[];
  volumeRest: number;
  volumeWs: number;
  /** Bid >= ask locally means our deltas have diverged; cleared by a resync. */
  crossed: boolean;
  marketStatus: string;
  marketResult: string;
  pendingDeltas: number;
  lastWsTs: number;
}

export function newMarketState(): MarketState {
  return {
    yesBids: new Map(),
    noBids: new Map(),
    myOrders: new Map(),
    estAhead: new Map(),
    bestYesBid: null,
    bestNoBid: null,
    bestYesAsk: null,
    bestNoAsk: null,
    midYesDeci: null,
    openOrders: 0,
    bidOrders: 0,
    askOrders: 0,
    positionMq: 0,
    avgCostMc: 0,
    realizedCash: 0,
    feesCash: 0,
    priceLevelStructure: 'tapered_deci_cent',
    priceRanges: [],
    volumeRest: 0,
    volumeWs: 0,
    crossed: false,
    marketStatus: 'open',
    marketResult: '',
    pendingDeltas: 0,
    lastWsTs: 0,
  };
}

export interface Bracket {
  ticker: string;
  account_id: string;
  entry_side: 'yes' | 'no';
  hedge_side: 'yes' | 'no';
  hedge_price_mc: MC;
  orig_mq: MQ;
  entry_filled_mq: MQ;
  hedged_mq: MQ;
  hedge_order_ids: string[];
}

/** [myQtyMq, bidMq, priceMc, askMq, queuePos, orderCount, side]; side 'p' is a simulated PHANTOM order. */
export type LadderRow = [MQ, MQ, MC, MQ, number | null, number, '' | 'y' | 'n' | 'b' | 'p'];

export interface PositionRow {
  ticker: string;
  positionMq: MQ;
  avgCostMc: number | null;
  midMc: number | null;
  settled: boolean;
  realizedCash: Cash;
  unrealizedCash: Cash | null;
  feesCash: Cash;
  pnlCash: Cash;
  deployedCash: Cash;
}

export interface AccountRow {
  id: string;
  name: string;
  parts: string[];
  realizedCash: Cash;
  unrealizedCash: Cash;
  feesCash: Cash;
  pnlCash: Cash;
  deployedCash: Cash;
  positionMq: MQ;
  positions: PositionRow[];
}

export interface UiSnapshot {
  type: 'snapshot';
  ticker: string;
  stats: {
    bestYesBid: MC | null;
    bestYesAsk: MC | null;
    midYesDeci: number | null;
    openOrders: number;
    bidOrders: number;
    askOrders: number;
    volume: number;
    positionMq: MQ;
    pnlCash: Cash | null;
    tickMc: number;
    /** Price grid override for non-standard ladders (e.g. an arb ladder from +99.9¢ to −99.9¢); both or neither. */
    priceHiMc?: MC;
    priceLoMc?: MC;
    /** Extra stat-box lines a venue wants shown (an arb's join and cross prices). */
    extra?: { label: string; value: string; color?: string }[];
    /** The two prices a click can cross at right now: `buy` lifts askQ-bidH, `sell` hits bidQ-askH. */
    arbCross?: { buyMc: MC | null; buyMq: MQ; sellMc: MC | null; sellMq: MQ };
    /** Local book shows bid >= ask — impossible on a matched exchange, so it
   *  means our deltas have diverged. Cleared by a resync. */
  crossed: boolean;
  marketStatus: string;
    marketResult: string;
  };
  rows: LadderRow[];
  orders: UiOrder[];
  /** account_id -> level_mc -> [bidFills, askFills] for today (PT). */
  fills: Record<string, Record<number, [number, number]>>;
  /** account_id -> {entry:[level_mc], hedge:[level_mc]} */
  brackets: Record<string, { entry: MC[]; hedge: MC[] }>;
  pnlByAccount: Record<string, PositionRow>;
}

export interface UiOrder {
  order_id: string;
  client_order_id: string;
  ticker: string;
  status: string;
  side: 'yes' | 'no' | '';
  price_mc: MC | null;
  remaining_mq: MQ;
  queue_position: number | null;
  created_time?: string;
  source: OrderSource;
  account_id: string | null;
}

export interface OrdersOutRow {
  ticker: string;
  account_id: string;
  account: string;
  bids: number;
  asks: number;
  bracket: number;
}

export interface SettledRow {
  ticker: string;
  result: string;
  pnlCash: Cash;
  settled_time: string;
}

export interface PnlTreeNode {
  name: string;
  children: Record<string, PnlTreeNode>;
  pnlCash: Cash;
  positionMq: MQ;
  id?: string;
}

export interface PnlSnapshot {
  type: 'pnl_update';
  balanceCash: Cash;
  totalPnlCash: Cash;
  accounts: AccountRow[];
  tree: PnlTreeNode;
  orders_out: OrdersOutRow[];
  settled_recent: SettledRow[];
  settled: SettledRow[];
}

export interface FillEvent {
  type: 'fill_event';
  ticker: string;
  account_id: string;
  ts: number;
  side: 'yes' | 'no';
  mq: MQ;
  yes_price_mc: MC;
  is_taker: boolean;
  /** The order this fill executed against, when the venue says. The arb
   *  engine matches fills to its resting quotes by this. */
  order_id?: string;
}
